import { CircularProgress, InputBase } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useRef } from 'react'
import { logInfo } from 'utils/logUtils'

const useStyles = makeStyles(theme => ({
  root: {
    padding: '8px 14px',
    borderBottom: '1px solid var(--border-color-7)',
    minWidth: '160px',
    display: 'flex',
    alignItems: 'center'
  },
  input: {
    fontFamily: 'Dazzed',
    color: theme.palette.primary.main,
    flex: 1
  }
}))

const TagsPickerInput = ({
  value,
  setValue,
  onEnterPressed = () => {},
  disabled = false,
  isLoading = false,
  showSuccess = false
}) => {
  const classes = useStyles()
  const keystrokeTimestamps = useRef([])
  const lastKeystrokeTime = useRef(null)

  const handleKeyPress = useCallback(
    e => {
      if (e.key === 'Enter') {
        onEnterPressed()
      }
    },
    [onEnterPressed]
  )

  const handleChange = useCallback(
    e => {
      const now = performance.now()
      const newValue = e.target.value

      // Track time since last keystroke
      const timeSinceLastKeystroke = lastKeystrokeTime.current ? now - lastKeystrokeTime.current : 0
      lastKeystrokeTime.current = now

      // Store keystroke timing for analysis
      keystrokeTimestamps.current.push({
        timestamp: now,
        timeSincePrevious: timeSinceLastKeystroke,
        valueLength: newValue.length
      })

      // Keep only last 20 keystrokes
      if (keystrokeTimestamps.current.length > 20) {
        keystrokeTimestamps.current.shift()
      }

      // Log if there's significant delay (>100ms suggests blocking)
      if (timeSinceLastKeystroke > 100 && keystrokeTimestamps.current.length > 1) {
        logInfo('[TagsPickerInput] PERF: Slow keystroke detected', {
          component: 'TagsPickerInput',
          event: 'slow_keystroke',
          timeSinceLastKeystrokeMs: timeSinceLastKeystroke,
          currentValueLength: newValue.length,
          previousValueLength: value?.length,
          recentKeystrokes: keystrokeTimestamps.current.slice(-5)
        })
      }

      // Always log keystroke for debugging (can be filtered in Datadog)
      logInfo('[TagsPickerInput] Input change', {
        component: 'TagsPickerInput',
        event: 'input_change',
        newValueLength: newValue.length,
        previousValueLength: value?.length,
        timeSinceLastKeystrokeMs: timeSinceLastKeystroke
      })

      const setValueStart = performance.now()
      setValue(newValue)
      const setValueDuration = performance.now() - setValueStart

      if (setValueDuration > 10) {
        logInfo('[TagsPickerInput] PERF: Slow setValue execution', {
          component: 'TagsPickerInput',
          event: 'slow_set_value',
          durationMs: setValueDuration,
          newValueLength: newValue.length
        })
      }
    },
    [setValue, value]
  )

  return (
    <div className={classes.root}>
      &#8288;
      <InputBase
        autoFocus
        value={value}
        onKeyPress={handleKeyPress}
        onChange={handleChange}
        className={classes.input}
        disabled={disabled}
      />
      {isLoading && <CircularProgress size={14} color="primary" />}
      {showSuccess && <CheckIcon style={{ fontSize: 16, color: '#4caf50' }} />}
    </div>
  )
}

export default TagsPickerInput
